import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef } from "react";
import { useFragment } from "react-relay";

import { useBookingPaymentCards } from "@/features/Square/hooks/useBookingPaymentCards/useBookingPaymentCards";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { canAddMemberHold } from "@/utils/memberHolds/canAddMemberHold";

import { initializeBookingWizardAtom } from "../../atoms/actions/initializeBookingWizard/initializeBookingWizardAtom";
import { BookingWizardDataAtom } from "../../atoms/stores";
import { useCompleteBooking } from "../../components/WizardFooter/hooks/useCompleteBooking/useCompleteBooking";
import { BookingWizard } from "../../types";
import { useValidDurations } from "../useValidDurations/useValidDurations";
import { useInitializeBookingWizardNode$key } from "./__generated__/useInitializeBookingWizardNode.graphql";
import { useInitializeBookingWizardSession$key } from "./__generated__/useInitializeBookingWizardSession.graphql";
import { useGetRequiredWizardSteps } from "./hooks/useGetRequiredWizardSteps";

type StandardBookingProps = {
  sessionRef: useInitializeBookingWizardSession$key | null;
  nodeRef: useInitializeBookingWizardNode$key | null;
  initializationOverides?: BookingWizard.BookingWizardInitializationOverides;
  onClose: () => void;
};

type DurationOption = { durationMins: number };

type InitialCourseLayoutOption = {
  readonly id: string;
  readonly code: string;
  readonly holeCount: BookingWizard.HoleCount;
  readonly name: string;
};

type DefaultCourseLayoutOption = {
  readonly id: string;
};

export const getInitialDurationMins = ({
  defaultDurationMins,
  durationOptions,
}: {
  defaultDurationMins: number | null | undefined;
  durationOptions: DurationOption[];
}): number | null => {
  const matchingDefault = durationOptions.find(
    (option) => option.durationMins === defaultDurationMins
  );
  if (matchingDefault) {
    return matchingDefault.durationMins;
  }

  return (
    durationOptions.find((option) => option.durationMins)?.durationMins ?? null
  );
};

const isInitialCourseLayoutOption = (
  courseLayout: InitialCourseLayoutOption | null | undefined
): courseLayout is InitialCourseLayoutOption => {
  return !!courseLayout?.id;
};

export const getInitialCourseLayout = ({
  defaultCourseLayout,
  initializationHoleCount,
  courseLayouts,
  preferDefaultCourseLayout = true,
}: {
  defaultCourseLayout: DefaultCourseLayoutOption | null | undefined;
  initializationHoleCount?: BookingWizard.HoleCount;
  courseLayouts: readonly (InitialCourseLayoutOption | null | undefined)[];
  preferDefaultCourseLayout?: boolean;
}): BookingWizard.CourseLayout | null => {
  const eligibleCourseLayouts = courseLayouts.filter(
    isInitialCourseLayoutOption
  );

  const overrideCourseLayout = initializationHoleCount
    ? eligibleCourseLayouts.find(
        (courseLayout) => courseLayout.holeCount === initializationHoleCount
      )
    : undefined;

  const selectedCourseLayout =
    overrideCourseLayout ??
    (preferDefaultCourseLayout && defaultCourseLayout
      ? eligibleCourseLayouts.find(
          (courseLayout) => courseLayout.id === defaultCourseLayout.id
        )
      : undefined) ??
    eligibleCourseLayouts[0] ??
    null;

  return selectedCourseLayout
    ? {
        id: selectedCourseLayout.id,
        code: selectedCourseLayout.code,
        holeCount: selectedCourseLayout.holeCount,
        name: selectedCourseLayout.name,
      }
    : null;
};

export const useInitializeBookingWizard = ({
  sessionRef,
  nodeRef,
  initializationOverides,
  onClose,
}: StandardBookingProps) => {
  /**
   * Gather Data / atom setters
   */
  const session = useFragment(
    useInitializeBookingWizardSessionFragmentString,
    sessionRef
  );
  const node = useFragment(
    useInitializeBookingWizardNodeFragmentString,
    nodeRef
  );

  // Track whether auto-completion has already been triggered
  const hasAutoCompletedRef = useRef(false);

  const initializeBookingWizard = useSetAtom(initializeBookingWizardAtom);
  const trackEvent = useTrackEvent();

  const [handleCompleteBooking] = useCompleteBooking(onClose);

  const bookingWizardData = useAtomValue(BookingWizardDataAtom);
  /**
   * Pluck / arrange specific data.
   */
  const startSlotId = node?.id;
  const facilityType = node?.agenda?.facility?.type;
  const initializationDurationMins = initializationOverides?.durationMins;
  const initializationGroupSize = initializationOverides?.groupSize;
  const initializationHoleCount = initializationOverides?.holeCount;

  // Determine the booking type based on facility
  const isGolfBooking = facilityType === "GOLF_COURSE";
  const bookingType = isGolfBooking ? "GolfBooking" : "DurationBooking";

  const isPrivate = node?.agenda?.facility?.club?.supportsPrivate ?? false;

  const ownerClientId = session?.currentClientProfile?.member?.id;
  const defaultRateId = node?.currentDefaultRate?.rate?.id;
  const isMissingRate =
    isGolfBooking &&
    session?.currentClientProfile?.member?.clientClass === "CUSTOMER" &&
    !defaultRateId;
  const defaultBookingRateId = node?.currentDefaultBookingRate?.[0]?.rate?.id;
  const defaultTransportationTypeId = session?.currentClientProfile?.member
    ?.preferredTransportationType?.enabled
    ? session?.currentClientProfile?.member?.preferredTransportationType?.id
    : null;
  const defaultCourseId = node?.course?.id;

  /**
   * This is duration specific since golf doesn't need a duration at all
   */
  const timeSlotDurationOptions = useValidDurations(node ?? null);
  const defaultDurationMins = node?.course?.defaultBookingDurationMins;
  const firstTimeSlotDurationOption = useMemo(() => {
    return getInitialDurationMins({
      defaultDurationMins,
      durationOptions: timeSlotDurationOptions,
    });
  }, [defaultDurationMins, timeSlotDurationOptions]);

  const cards = useBookingPaymentCards(session, node?.agenda?.date);
  const defaultPaymentCardOnFileId = cards.find((card) => card.default)?.id;

  const shouldAddPaymentCardOnFile = !!defaultPaymentCardOnFileId;
  const paymentCardOnFileId = shouldAddPaymentCardOnFile
    ? defaultPaymentCardOnFileId
    : undefined;

  const shouldRequirePlayerCounts = !!node?.requirePlayerCounts;

  // Get capacity information
  //This is sort of golf specific since duration never has used capacity
  //TODO: clean this up
  const standardCapacity = node?.standardCapacity ?? 4;
  const usedCapacity = node?.usedCapacity ?? 0;

  const groupSize = standardCapacity - usedCapacity;

  const currentBookingPlayerRound: BookingWizard.GolfRound = useMemo(
    () => ({
      index: 0,
      id: null,
      type: "Golf",
      courseLayout: null,
      transportationType: null,
      rate: null,
      player: {
        id: session?.currentClientProfile?.member?.id ?? "",
        firstName: session?.currentClientProfile?.member?.firstName ?? "",
        lastName: session?.currentClientProfile?.member?.lastName ?? "",
        profileImagePublicId:
          session?.currentClientProfile?.member?.profileImage?.publicId ?? null,
        clientType: isPrivate ? "member" : "customer",
        membershipTypeId:
          session?.currentClientProfile?.member?.membershipType?.id ?? "",
      },
    }),
    [
      session?.currentClientProfile?.member?.firstName,
      session?.currentClientProfile?.member?.lastName,
      session?.currentClientProfile?.member?.id,
      session?.currentClientProfile?.member?.profileImage?.publicId,
      session?.currentClientProfile?.member?.membershipType?.id,
      isPrivate,
    ]
  );

  /**
   * Build initial booking data based on type
   */
  const initialBookingWizardData: BookingWizard.BookingWizardData | null =
    useMemo(() => {
      if (!ownerClientId || !startSlotId) {
        return null;
      }

      const baseWizardData = {
        ownerClientId,
        startSlotId,
        groupSize,
        paymentCardOnFileId,
        isPrivate,
        canAddMemberHold: canAddMemberHold({
          memberHoldEnabled: node?.agenda?.facility?.memberHoldEnabled,
          memberHoldDeadlineHours:
            node?.agenda?.facility?.memberHoldDeadlineHours,
          bookingDate: node?.agenda?.date,
          bookingTime: node?.time,
        }),
      };

      // Golf booking
      if (bookingType === "GolfBooking") {
        const permittedCourseLayouts =
          node?.permittedCourseLayouts?.edges?.flatMap((edge) =>
            edge?.node ? [edge.node] : []
          ) ?? [];
        const courseLayouts =
          node?.course?.courseLayouts?.edges?.flatMap((edge) =>
            edge?.node ? [edge.node] : []
          ) ?? [];
        const shouldUsePrivateFallbackCourseLayouts =
          isPrivate && permittedCourseLayouts.length === 0;
        const privateCourseLayouts = shouldUsePrivateFallbackCourseLayouts
          ? courseLayouts
          : permittedCourseLayouts;
        const selectedCourseLayout = getInitialCourseLayout({
          defaultCourseLayout: node?.course?.defaultCourseLayout,
          initializationHoleCount,
          courseLayouts: isPrivate
            ? privateCourseLayouts
            : permittedCourseLayouts,
          preferDefaultCourseLayout: !shouldUsePrivateFallbackCourseLayouts,
        });

        const currentRoundCourseLayout = selectedCourseLayout
          ? {
              id: selectedCourseLayout.id,
              holeCount: selectedCourseLayout.holeCount,
              name: selectedCourseLayout.name,
            }
          : null;

        return {
          ...baseWizardData,
          type: "GolfBooking",
          ...(selectedCourseLayout
            ? { courseLayout: selectedCourseLayout }
            : {}),
          transportationTypeId: defaultTransportationTypeId,
          rateId: defaultRateId ?? null,
          rounds: [
            {
              ...currentBookingPlayerRound,
              courseLayout: currentRoundCourseLayout,
              transportationType: defaultTransportationTypeId
                ? {
                    id: defaultTransportationTypeId,
                  }
                : null,
            },
          ],
          defaultTransportationTypeId: defaultTransportationTypeId,
          ...(initializationGroupSize !== undefined && {
            groupSize: initializationGroupSize,
          }),
          ...(initializationHoleCount !== undefined && {
            holeCount: initializationHoleCount,
          }),
        };
      }

      // Duration booking (non-lesson)
      return {
        ...baseWizardData,
        type: "DurationBooking",
        durationMins: firstTimeSlotDurationOption ?? null,
        rounds: [{ ...currentBookingPlayerRound, type: "Duration" }],
        shouldRequirePlayerCounts,
        bookingRateId: defaultBookingRateId ?? null,
        playerRateId: defaultRateId ?? null,
        courseId: defaultCourseId ?? null,
        ...(initializationDurationMins !== undefined && {
          durationMins: initializationDurationMins,
        }),
        ...(initializationGroupSize !== undefined && {
          groupSize: initializationGroupSize,
        }),
        ...(initializationHoleCount !== undefined && {
          holeCount: initializationHoleCount,
        }),
      };
    }, [
      ownerClientId,
      startSlotId,
      groupSize,
      paymentCardOnFileId,
      bookingType,
      defaultTransportationTypeId,
      defaultRateId,
      currentBookingPlayerRound,
      firstTimeSlotDurationOption,
      shouldRequirePlayerCounts,
      defaultBookingRateId,
      defaultCourseId,
      isPrivate,
      node?.agenda?.facility?.memberHoldEnabled,
      node?.agenda?.facility?.memberHoldDeadlineHours,
      node?.time,
      node?.agenda?.date,
      initializationDurationMins,
      initializationGroupSize,
      initializationHoleCount,
      node?.course?.courseLayouts?.edges,
      node?.course?.defaultCourseLayout,
      node?.permittedCourseLayouts?.edges,
    ]);

  const requiredWizardSteps: BookingWizard.BookingWizardStepTitle[] =
    useGetRequiredWizardSteps({
      sessionRef: session?.currentClientProfile ?? null,
      groupSize,
      requirePlayerCounts: !!node?.requirePlayerCounts,
      isDurationFacility: facilityType !== "GOLF_COURSE",
      isPrivate,
      isGolf: isGolfBooking,
      openSlots: standardCapacity - usedCapacity,
    });

  useEffect(() => {
    if (initialBookingWizardData && !isMissingRate) {
      trackEvent("BookingWizard.Initialize", {
        initialBookingWizardData,
        requiredSteps: requiredWizardSteps,
      });
      initializeBookingWizard({
        initialBookingWizardData,
        requiredWizardSteps,
      });
    }
  }, [
    initializeBookingWizard,
    isMissingRate,
    initialBookingWizardData,
    requiredWizardSteps,
    trackEvent,
  ]);

  useEffect(() => {
    if (
      !isMissingRate &&
      requiredWizardSteps.length === 0 &&
      bookingWizardData &&
      !hasAutoCompletedRef.current
    ) {
      hasAutoCompletedRef.current = true;
      handleCompleteBooking();
    }
  }, [
    isMissingRate,
    requiredWizardSteps,
    bookingWizardData,
    handleCompleteBooking,
  ]);

  return { isMissingRate };
};

export const useInitializeBookingWizardSessionFragmentString = graphql`
  fragment useInitializeBookingWizardSession on Session
  @argumentDefinitions(forSlotId: { type: "ID", defaultValue: null }) {
    ...useBookingPaymentCards_session @arguments(bookingDate: $currentDate)
    currentClientProfile {
      ...useGetRequiredWizardSteps @arguments(forSlotId: $forSlotId)
      member {
        clientClass
        membershipType {
          id
        }
        ... on Member {
          id
          firstName
          lastName
          profileImage {
            # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
            publicId: renderingKey
          }
          preferredTransportationType(slotId: $slotId) {
            id
            enabled
          }
        }
        ... on Customer {
          id
          firstName
          lastName
          profileImage {
            # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
            publicId: renderingKey
          }
          preferredTransportationType(slotId: $slotId) {
            id
            enabled
          }
        }
      }
    }
  }
`;

export const useInitializeBookingWizardNodeFragmentString = graphql`
  fragment useInitializeBookingWizardNode on Node {
    ...useValidDurationsNode
    ... on TimeSlot {
      id
      time
      standardCapacity: capacity(bookingType: STANDARD)
      usedCapacity
      requirePlayerCounts
      agenda {
        date
        facility {
          memberHoldEnabled
          memberHoldDeadlineHours
          type
          club {
            supportsPrivate
          }
        }
      }
      currentDefaultBookingRate: bookingRates(
        membershipTypeId: $membershipTypeId
        type: CUSTOMER
      ) {
        rate {
          id
        }
      }
      currentDefaultRate: playerRateForMembershipType(
        membershipTypeId: $membershipTypeId
        type: CUSTOMER
      ) {
        rate {
          id
        }
      }
      course {
        id
        name
        defaultBookingDurationMins
        defaultCourseLayout {
          id
          code
          holeCount
          name
        }
        courseLayouts {
          edges {
            node {
              id
              code
              holeCount
              name
            }
          }
        }
      }
      permittedCourseLayouts(memberVisible: true) {
        edges {
          node {
            id
            code
            holeCount
            name
          }
        }
      }
    }
  }
`;
